import { useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/api/client";
import { UtensilsCrossed, Plus, Edit3, Trash2, X, Check, Image as ImageIcon, Power, Globe, Scale } from "lucide-react";
import ImagePicker from "@/components/ImagePicker";
import { UNIT_LABELS } from "@/components/AdminCategorySettingsModal";

/**
 * Shared menu editor used by both Admin and Restaurant dashboards.
 *
 * Props:
 *  - scope: "admin" | "restaurant"
 *      → "admin"      → uses /menu (list, filter by restaurant_id query), /menu POST, /menu/{id} PATCH/DELETE
 *      → "restaurant" → uses /restaurant/menu (server injects restaurant_id from token)
 *  - restaurantId?: string — required when scope="admin" (item scope). Ignored when scope="restaurant".
 *  - title?: string — header title (default "محرر المنيو")
 */
export default function MenuEditor({ scope, restaurantId, title = "محرر المنيو" }) {
  const isAdmin = scope === "admin";
  const listUrl = isAdmin ? `/menu${restaurantId ? `?restaurant_id=${restaurantId}` : ""}` : "/restaurant/menu";
  const createUrl = isAdmin ? "/menu" : "/restaurant/menu";
  const itemUrl = (id) => (isAdmin ? `/menu/${id}` : `/restaurant/menu/${id}`);

  const emptyForm = { name: "", description: "", price: 0, category: "", image: "", extras: [], available: true, unit: "piece" };

  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | item object
  const [form, setForm] = useState(emptyForm);
  const [extraDraft, setExtraDraft] = useState({ name: "", price: 0 });
  const [filterCat, setFilterCat] = useState("all");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Unit-of-measure config resolved from the shop's category profile.
  // `allowedUnits` gates the selector so a vegetables shop only sees kg/gram/bundle,
  // and `defaultUnit` seeds new items. Falls back to piece-only until loaded.
  const [defaultUnit, setDefaultUnit] = useState("piece");
  const [allowedUnits, setAllowedUnits] = useState(["piece"]);

  // Resolve the shop's `shop_type` → category profile once per open.
  // Skip the shop_type→profile lookup entirely when admin hasn't picked a
  // shop yet (button is disabled in that state anyway).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let shopType = null;
        if (isAdmin) {
          if (!restaurantId) return;
          const { data } = await api.get(`/restaurants/${restaurantId}`);
          shopType = data?.shop_type || "restaurant";
        } else {
          const { data } = await api.get("/restaurant/me");
          shopType = data?.restaurant?.shop_type || data?.shop_type || "restaurant";
        }
        if (!shopType || cancelled) return;
        const { data: prof } = await api.get(`/category-profiles/${shopType}`);
        if (cancelled) return;
        const au = Array.isArray(prof?.allowed_units) && prof.allowed_units.length
          ? prof.allowed_units : ["piece"];
        const du = au.includes(prof?.default_unit) ? prof.default_unit : au[0];
        setAllowedUnits(au);
        setDefaultUnit(du);
      } catch {
        // Silent: fall back to piece-only if the shop or profile lookup fails.
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, restaurantId]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(listUrl);
      setItems(data);
      const uniq = Array.from(new Set(data.map((m) => m.category).filter(Boolean))).sort();
      setCats(uniq);
    } catch (e) {
      setErr(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [listUrl]);

  useEffect(() => { refresh(); }, [refresh]);

  const openNew = () => {
    if (isAdmin && !restaurantId) { setErr("يرجى اختيار مطعم أولاً"); return; }
    // Seed the new-item form with the shop's default unit so the owner
    // doesn't have to pick it every time — they can still override.
    setForm({ ...emptyForm, unit: defaultUnit });
    setEditing("new");
    setErr("");
  };
  const openEdit = (it) => { setForm({ ...it, extras: it.extras || [], unit: it.unit || defaultUnit }); setEditing(it); setErr(""); };

  const save = async () => {
    if (!form.name || !form.category || form.price <= 0) {
      setErr("الاسم والفئة والسعر مطلوبة"); return;
    }
    setBusy(true); setErr("");
    try {
      const payload = {
        name: form.name,
        description: form.description || "",
        price: parseFloat(form.price) || 0,
        category: form.category,
        image: form.image || "",
        extras: form.extras || [],
        available: form.available,
        unit: allowedUnits.includes(form.unit) ? form.unit : defaultUnit,
      };
      if (editing === "new") {
        if (isAdmin) payload.restaurant_id = restaurantId;
        await api.post(createUrl, payload);
      } else {
        // Atomic edit via PATCH
        await api.patch(itemUrl(editing.id), payload);
      }
      setEditing(null);
      await refresh();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`حذف الصنف "${it.name}"؟`)) return;
    setBusy(true);
    try { await api.delete(itemUrl(it.id)); await refresh(); }
    catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const toggleAvailable = async (it) => {
    const next = !it.available;
    // Optimistic update
    setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, available: next } : x)));
    setErr("");
    try { await api.patch(itemUrl(it.id), { available: next }); }
    catch (e) {
      // Rollback on failure
      setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, available: !next } : x)));
      setErr(extractError(e));
    }
  };

  const addExtra = () => {
    if (!extraDraft.name || extraDraft.price < 0) return;
    setForm((f) => ({
      ...f,
      extras: [...(f.extras || []), { id: `e${Date.now()}`, name: extraDraft.name, price: parseFloat(extraDraft.price) || 0 }]
    }));
    setExtraDraft({ name: "", price: 0 });
  };
  const applyExtraToAll = async () => {
    const name = (extraDraft.name || "").trim();
    const price = parseFloat(extraDraft.price) || 0;
    if (!name || price < 0) {
      setErr("أدخل اسم الإضافة وسعرها أولاً");
      return;
    }
    if (isAdmin && !restaurantId) {
      setErr("اختر مطعماً أولاً");
      return;
    }
    if (!window.confirm(`سيتم إضافة "${name}" (+${price} د.ل) إلى كل الأصناف في المطعم. متابعة؟`)) return;
    setBusy(true); setErr("");
    try {
      const url = isAdmin ? "/menu/extras/apply-to-all" : "/restaurant/menu/extras/apply-to-all";
      const payload = { name, price, ...(isAdmin ? { restaurant_id: restaurantId } : {}) };
      const { data } = await api.post(url, payload);
      // 1) Reset the input so the user sees the action took effect.
      setExtraDraft({ name: "", price: 0 });
      // 2) Immediately append the extra to the currently-open form so it
      //    shows in the "الإضافات" list without a manual refresh — works for
      //    both existing items (editing === object) and new drafts ("new").
      setForm((f) => {
        const existing = f.extras || [];
        const dup = existing.some((e) => (e.name || "").trim() === name && Number(e.price) === price);
        if (dup) return f;
        return { ...f, extras: [...existing, { id: `e${Date.now()}`, name, price }] };
      });
      // 3) Sync the underlying list in the background.
      await refresh();
      alert(`✅ تمت الإضافة إلى ${data.items_updated} صنف (من إجمالي ${data.items_total})`);
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };
  const removeExtra = (id) => setForm((f) => ({ ...f, extras: f.extras.filter((e) => e.id !== id) }));

  const filtered = filterCat === "all" ? items : items.filter((i) => i.category === filterCat);
  const canAdd = isAdmin ? !!restaurantId : true;

  return (
    <div data-testid="menu-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-brand text-white flex items-center justify-center"><UtensilsCrossed size={20}/></div>
          <div>
            <h2 className="text-xl font-black" style={{fontFamily:'Cairo'}}>{title}</h2>
            <div className="text-xs text-[var(--text-secondary)] mt-0.5">{items.length} صنف · {cats.length} فئة</div>
          </div>
        </div>
        <button onClick={openNew} disabled={!canAdd} className="px-4 py-2.5 rounded-xl bg-primary-brand text-white font-bold text-sm flex items-center gap-2 talabya-shadow-float disabled:opacity-60" data-testid="btn-add-menu">
          <Plus size={16}/> صنف جديد
        </button>
      </div>

      {/* Category filter */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-4">
        {["all", ...cats].map((c) => (
          <button key={c} onClick={() => setFilterCat(c)}
            className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap ${filterCat === c ? "bg-primary-brand text-white" : "bg-white border border-[var(--border)] text-[var(--text-primary)] hover:border-primary-brand"}`}
            data-testid={`mfilter-${c}`}>
            {c === "all" ? "الكل" : c}
          </button>
        ))}
      </div>

      {err && !editing && <div className="bg-red-50 text-red-600 px-4 py-2 rounded-xl text-sm mb-3">{err}</div>}

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-56 rounded-2xl shimmer"/>)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center talabya-shadow-soft">
          <UtensilsCrossed size={40} className="mx-auto text-[var(--text-secondary)] mb-3"/>
          <div className="text-lg font-bold" style={{fontFamily:'Cairo'}}>لا توجد أصناف</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((it) => (
            <div key={it.id} className={`bg-white rounded-2xl talabya-shadow-soft overflow-hidden border-2 transition ${it.available ? "border-green-200" : "border-red-200 opacity-90"}`} data-testid={`menu-item-${it.id}`}>
              <div className="relative h-36 bg-[var(--surface-alt)]">
                {it.image
                  ? <img src={it.image} alt={it.name} className={`w-full h-full object-cover ${it.available ? "" : "grayscale"}`}/>
                  : <div className="w-full h-full flex items-center justify-center text-[var(--text-secondary)]"><ImageIcon size={32}/></div>}
                <div className="absolute top-2 right-2 bg-white/95 text-primary-brand text-[10px] font-bold px-2 py-0.5 rounded-full">{it.category}</div>
                {!it.available && (
                  <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
                    <div className="bg-red-600 text-white text-[11px] font-black px-3 py-1 rounded-full shadow-lg" style={{fontFamily:'Cairo'}}>
                      غير متوفر الآن
                    </div>
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate" style={{fontFamily:'Cairo'}}>{it.name}</div>
                    <div className="text-xs text-[var(--text-secondary)] line-clamp-1 mt-0.5">{it.description}</div>
                    <div className="text-primary-brand font-black text-lg mt-1" style={{fontFamily:'Cairo'}}>
                      {it.price} د.ل
                      {it.unit && it.unit !== "piece" && (
                        <span className="text-xs font-bold text-[var(--text-secondary)] ms-1">/ {UNIT_LABELS[it.unit]?.short || it.unit}</span>
                      )}
                    </div>
                    {it.extras?.length > 0 && (
                      <div className="text-[10px] text-[var(--text-secondary)] mt-1">{it.extras.length} إضافة</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => openEdit(it)} className="w-8 h-8 rounded-lg bg-[var(--surface-alt)] hover:bg-[var(--border)] flex items-center justify-center" data-testid={`medit-${it.id}`}>
                      <Edit3 size={14}/>
                    </button>
                    <button onClick={() => remove(it)} disabled={busy} className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center" data-testid={`mdel-${it.id}`}>
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </div>
                {/* Availability quick toggle */}
                <button
                  onClick={() => toggleAvailable(it)}
                  aria-pressed={it.available}
                  className={`mt-3 w-full py-2 rounded-xl text-xs font-black flex items-center justify-center gap-2 transition ${it.available ? "bg-green-500 hover:bg-green-600 text-white" : "bg-red-500 hover:bg-red-600 text-white"}`}
                  data-testid={`mavail-${it.id}`}
                  title={it.available ? "اضغط لإيقاف التوفر" : "اضغط لتفعيل التوفر"}
                >
                  <Power size={14}/>
                  {it.available ? "متوفر — اضغط للإيقاف" : "غير متوفر — اضغط للتفعيل"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setEditing(null)} data-testid="menu-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-[var(--border)] z-10">
              <h2 className="text-xl font-black" style={{fontFamily:'Cairo'}}>{editing === "new" ? "صنف جديد" : "تعديل الصنف"}</h2>
              <button onClick={() => setEditing(null)} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center"><X size={16}/></button>
            </div>
            <div className="p-5 flex flex-col gap-3">
              <div>
                <label className="text-sm font-bold block mb-1">اسم الصنف *</label>
                <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                  data-testid="mform-name"/>
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">الوصف</label>
                <textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right resize-none"
                  data-testid="mform-desc"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-bold block mb-1">السعر (د.ل) *</label>
                  <input type="number" step="0.5" value={form.price} onChange={(e) => setForm({...form, price: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                    data-testid="mform-price"/>
                </div>
                <div>
                  <label className="text-sm font-bold block mb-1">الفئة *</label>
                  <input list={`menu-cats-${scope}`} value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                    placeholder="برجر، بيتزا..." data-testid="mform-cat"/>
                  <datalist id={`menu-cats-${scope}`}>
                    {cats.map((c) => <option key={c} value={c}/>)}
                  </datalist>
                </div>
              </div>

              {/* Unit-of-measure — chip picker sourced from the shop's category
                  profile. Hidden entirely for restaurants (single-unit "piece"
                  fallback) to keep the form clean. */}
              {allowedUnits.length > 1 && (
                <div>
                  <label className="text-sm font-bold flex items-center gap-1 mb-1">
                    <Scale size={13}/> وحدة القياس
                    <span className="text-[10px] text-[var(--text-secondary)] font-normal">(السعر لكل {UNIT_LABELS[form.unit]?.ar || form.unit})</span>
                  </label>
                  <div className="flex flex-wrap gap-1" data-testid="mform-unit-chips">
                    {allowedUnits.map((u) => {
                      const on = form.unit === u;
                      return (
                        <button
                          key={u}
                          type="button"
                          onClick={() => setForm({ ...form, unit: u })}
                          className={`px-3 py-1.5 rounded-full text-xs font-black border transition ${
                            on
                              ? "bg-primary-brand text-white border-primary-brand"
                              : "bg-white text-[var(--text-secondary)] border-[var(--border)] hover:border-primary-brand/60"
                          }`}
                          data-testid={`mform-unit-${u}`}
                          aria-pressed={on}
                        >
                          {UNIT_LABELS[u]?.ar || u}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <label className="text-sm font-bold block mb-2">صورة الصنف</label>
                <ImagePicker value={form.image} onChange={(v) => setForm({...form, image: v})} label="صورة الصنف" height="h-32"/>
              </div>

              <div className="bg-[var(--surface-alt)] rounded-2xl p-3">
                <div className="text-sm font-bold mb-2">الإضافات ({form.extras?.length || 0})</div>
                <div className="flex flex-col gap-2 mb-2">
                  {(form.extras || []).map((e) => (
                    <div key={e.id} data-testid="extra-row" className="flex items-center justify-between bg-white rounded-lg p-2 text-sm">
                      <span className="font-bold">{e.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-primary-brand font-bold">+{e.price} د.ل</span>
                        <button onClick={() => removeExtra(e.id)} className="text-red-500"><X size={14}/></button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={extraDraft.name} onChange={(e) => setExtraDraft({...extraDraft, name: e.target.value})}
                    className="flex-1 px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-sm text-right"
                    placeholder="اسم الإضافة" data-testid="mform-extra-name"/>
                  <input type="number" step="0.5" value={extraDraft.price} onChange={(e) => setExtraDraft({...extraDraft, price: e.target.value})}
                    className="w-20 px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-sm text-center"
                    placeholder="السعر" data-testid="mform-extra-price"/>
                  <button type="button" onClick={addExtra} className="px-3 py-2 rounded-lg bg-primary-brand text-white text-sm font-bold" data-testid="mform-extra-add" title="إضافة إلى هذا الصنف فقط">
                    <Plus size={14}/>
                  </button>
                </div>
                <button type="button" onClick={applyExtraToAll} disabled={busy || !extraDraft.name}
                  className="mt-2 w-full px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-bold flex items-center justify-center gap-1.5"
                  data-testid="mform-extra-apply-all"
                  title="أضف هذه الإضافة إلى جميع الأصناف في المطعم">
                  <Globe size={13}/> تطبيق على كل الأصناف
                </button>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.available} onChange={(e) => setForm({...form, available: e.target.checked})} className="accent-primary-brand w-4 h-4"/>
                <span className="text-sm font-bold">متاح (يظهر للزبائن)</span>
              </label>

              {err && <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm">{err}</div>}

              <div className="flex gap-2 mt-2">
                <button onClick={() => setEditing(null)} className="flex-1 py-3 rounded-xl bg-[var(--surface-alt)] font-bold text-sm">إلغاء</button>
                <button onClick={save} disabled={busy}
                  className="flex-[2] py-3 rounded-xl bg-primary-brand text-white font-bold text-sm talabya-shadow-float disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="mform-save">
                  <Check size={16}/> {busy ? "جاري الحفظ..." : "حفظ"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
